import type { SemanticColorName } from '@brokerbay/elements-web';
import { Flex, Paragraph, Text, Title } from '@brokerbay/elements-web';
import { Text as NewText } from '@bb/elements-web-rebranding';
import type { AnnouncementType } from 'api/AnnouncementController';
import moment from 'moment';
import React, { useMemo } from 'react';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import styled from 'styled-components';
import { featureEnabled } from 'utils';

type AnnouncementDetailsConfig = {
  text: string;
  color: SemanticColorName;
  actionText: string;
};

const getDetailsConfig = (
  type: AnnouncementItemDetailsProps['type'],
): AnnouncementDetailsConfig => {
  switch (type) {
    case 'draft':
      return { text: 'Draft', color: 'primary', actionText: 'Saved As Draft' };
    case 'sent':
      return { text: 'Sent', color: 'green', actionText: 'Sent' };
    case 'scheduled':
      return {
        text: 'Scheduled',
        color: 'grey',
        actionText: 'Scheduled For',
      };
    default:
      return { text: '', color: 'grey', actionText: '' };
  }
};

type AnnouncementItemDetailsProps = {
  subject: string;
  type: AnnouncementType;
  time: Date;
  userName: string;
  body: string;
};

const AnnouncementItemDetails = ({
  subject,
  type,
  time,
  userName,
  body,
}: AnnouncementItemDetailsProps) => {
  const config = getDetailsConfig(type);

  // Memoize component selection based on feature flag
  const hasSupraRebrandingFeature = useMemo(
    () => featureEnabled('supraRebrandingFeature', 1),
    [],
  );

  const StyledText = useMemo(
    () => (hasSupraRebrandingFeature ? StyledTextNew : StyledTextOld),
    [hasSupraRebrandingFeature],
  );

  return (
    <DetailsContainer wrap="wrap">
      <StyledTitle level={4} dataCy="announcement-item-details-title">
        {subject}
        <StyledText size="small" color={config.color}>
          {config.text}
        </StyledText>
      </StyledTitle>
      <StyledParagraph>
        <Text thickness="bold">{config.actionText}: </Text>
        {moment(time).format('MMM Do YYYY, HH:mm')} by <i>{userName}</i>
      </StyledParagraph>
      <FroalaEditorView model={body} />
    </DetailsContainer>
  );
};

const DetailsContainer = styled(Flex)`
  flex: 1 0 60%;
  min-width: 0;
  && .fr-view {
    max-width: 100%;
    white-space: pre-wrap;
  }
`;

const StyledTitle = styled(Title)`
  && {
    width: 100%;
    text-transform: uppercase;
    text-decoration: underline;
    font-family: 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 600;
    font-size: 18px;
  }
`;

// duplicated styled component to handle rebranding
const StyledTextOld = styled(Text)`
  && {
    white-space: pre-wrap;
    display: inline-block;
    text-decoration: none;
    padding-left: 3px;
  }
`;

const StyledTextNew = styled(NewText)`
  && {
    white-space: pre-wrap;
    display: inline-block;
    text-decoration: none;
    padding-left: 3px;
  }
`;

const StyledParagraph = styled(Paragraph)`
  && {
    flex: 0 0 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    margin-bottom: 1em;
  }
`;

export default AnnouncementItemDetails;
